import React, { useState, useEffect } from 'react';
import { getRedmineProjects, getRedminePriorities, createRedmineIssue, getDevOpsMembers } from './api';
import './Enchufeitor.css';

function Enchufeitor() {
  const [projects, setProjects] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [devopsMembers, setDevopsMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    project_id: '',
    assigned_to_id: '',
    subject: '',
    description: '',
    priority_id: 2, // Normal por defecto
    due_date: '',
    estimated_hours: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [projectsRes, prioritiesRes, membersRes] = await Promise.all([
        getRedmineProjects(),
        getRedminePriorities(),
        getDevOpsMembers()
      ]);

      setProjects(projectsRes.data);
      setPriorities(prioritiesRes.data);
      setDevopsMembers(membersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Error al cargar datos desde Redmine');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.project_id || !formData.assigned_to_id || !formData.subject) {
      setError('Por favor completa todos los campos obligatorios');
      return;
    }

    try {
      setCreating(true);
      setError(null);
      setSuccess(null);

      // Crear FormData para enviar archivo
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Agregar archivo si existe
      if (selectedFile) {
        formDataToSend.append('attachment', selectedFile);
      }

      const response = await createRedmineIssue(formDataToSend);

      setSuccess({
        message: `Tarea enchufada exitosamente!`,
        issueId: response.data.issue.id,
        redmineUrl: response.data.redmine_url
      });

      // Limpiar formulario
      setFormData({
        project_id: '',
        assigned_to_id: '',
        subject: '',
        description: '',
        priority_id: 2,
        due_date: '',
        estimated_hours: ''
      });
      setSearchTerm('');
      setSelectedFile(null);
    } catch (error) {
      console.error('Error creating issue:', error);
      setError(error.response?.data?.details || 'Error al crear la tarea en Redmine');
    } finally {
      setCreating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Filtrar proyectos por término de búsqueda
  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.identifier.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Obtener el proyecto seleccionado
  const selectedProject = projects.find(p => p.id === parseInt(formData.project_id));

  // Obtener el miembro seleccionado
  const selectedMember = devopsMembers.find(m => m.id === parseInt(formData.assigned_to_id));

  if (loading) {
    return (
      <div className="enchufeitor-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando Enchufeitor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="enchufeitor-container">
      <div className="enchufeitor-header">
        <div className="header-content">
          <h1>Enchufeitor</h1>
          <p className="subtitle">Crear Tareas de Sistemas en Redmine</p>
        </div>
        <div className="header-icon">
          <span className="icon-large">🔌</span>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          <div className="success-content">
            <div>{success.message}</div>
            <a
              href={success.redmineUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="redmine-link"
            >
              Ver Issue #{success.issueId} en Redmine →
            </a>
          </div>
          <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      <div className="enchufeitor-form-container">
        <form onSubmit={handleSubmit} className="enchufeitor-form">
          <div className="form-section">
            <h3>1. Seleccionar Proyecto</h3>
            <div className="form-group">
              <label htmlFor="search-project">Buscar proyecto</label>
              <input
                type="text"
                id="search-project"
                placeholder="Escribe para buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="project_id">Proyecto *</label>
              <select
                id="project_id"
                name="project_id"
                value={formData.project_id}
                onChange={handleChange}
                required
                className="select-input"
              >
                <option value="">-- Seleccionar Proyecto --</option>
                {filteredProjects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              {selectedProject && (
                <small className="form-hint">
                  Identificador: {selectedProject.identifier}
                </small>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>2. Asignar Miembro del Equipo</h3>
            <div className="form-group">
              <label htmlFor="assigned_to_id">Asignar a *</label>
              <select
                id="assigned_to_id"
                name="assigned_to_id"
                value={formData.assigned_to_id}
                onChange={handleChange}
                required
                className="select-input"
              >
                <option value="">-- Seleccionar Miembro --</option>
                {devopsMembers.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
              {selectedMember && (
                <small className="form-hint">
                  Email: {selectedMember.email}
                </small>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>3. Detalles de la Tarea</h3>
            <div className="form-group">
              <label htmlFor="subject">Asunto *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Ej: Configurar backup automático en servidor"
                required
                className="text-input"
                maxLength={255}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Descripción</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe los detalles de la tarea..."
                rows={6}
                className="textarea-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="priority_id">Prioridad</label>
                <select
                  id="priority_id"
                  name="priority_id"
                  value={formData.priority_id}
                  onChange={handleChange}
                  className="select-input"
                >
                  {priorities.map(priority => (
                    <option key={priority.id} value={priority.id}>
                      {priority.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="due_date">Fecha de vencimiento</label>
                <input
                  type="date"
                  id="due_date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleChange}
                  className="date-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="estimated_hours">Tiempo estimado (horas)</label>
                <input
                  type="number"
                  id="estimated_hours"
                  name="estimated_hours"
                  value={formData.estimated_hours}
                  onChange={handleChange}
                  placeholder="Ej: 4"
                  min="0"
                  step="0.5"
                  className="text-input"
                />
                <small className="form-hint">Opcional: Estimación en horas (0.5, 1, 2, etc.)</small>
              </div>

              <div className="form-group">
                <label htmlFor="attachment">Archivo adjunto</label>
                <input
                  type="file"
                  id="attachment"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="text-input"
                />
                {selectedFile && (
                  <small className="form-hint">Archivo: {selectedFile.name}</small>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={creating || !formData.project_id || !formData.assigned_to_id || !formData.subject}
              className="btn-enchufar"
            >
              {creating ? (
                <>
                  <span className="spinner-small"></span>
                  Enchufando...
                </>
              ) : (
                <>
                  <span className="btn-icon">🔌</span>
                  Enchufar Tarea
                </>
              )}
            </button>
          </div>
        </form>

        <div className="info-panel">
          <h3>ℹ️ Información</h3>
          <ul>
            <li>Las tareas se crean como tipo "Tarea de sistemas" en Redmine</li>
            <li>El issue aparecerá creado por ti (tu usuario de Redmine)</li>
            <li>El miembro asignado recibirá una notificación por email</li>
            <li>Solo los administradores pueden usar Enchufeitor</li>
            <li>Todos los proyectos activos están disponibles</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Enchufeitor;
